import {ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, Renderer2} from '@angular/core';
import {TaxPlanningService} from "../tpiq-services/tax-planning.service";
import {NgxScrollEvent} from "../directives/ngx-scroll-event.directive";
import {SwitchBoard} from "../services/SwitchBoard";
import {takeUntil} from "rxjs/operators";
import {Session} from "../services/Session";
import {environment} from "../../environments/environment";
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {MatIconModule} from "@angular/material/icon";

// The Knolli widget script appends these elements to <body> when it runs (CU-868mbzat6).
const KNOLLI_WIDGET_SELECTORS = ['.iframe-button', '.knolli-widget-iframe-drawer', '.knolli-widget-banner'];
const KNOLLI_CONTEXT_REPOST_MS = 1500;

@Component({
  selector: 'app-tpiq-chat-bot',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './tpiq-chat-bot.component.html',
  styleUrl: './tpiq-chat-bot.component.scss'
})
export class TpiqChatBotComponent implements OnInit, OnDestroy {
  org: string = Session.getOrg();
  user: any = {};
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  showChatPannel = false;
  smarterCodesframe: any;
  botUrl = "";
  currentEnvironment: any;
  showEscalation = false;
  escalationMessage = '';
  private knolliStageScript?: HTMLScriptElement;
  private knolliScriptSettled = false;
  private footerInterval?: ReturnType<typeof setInterval>;
  private destroyed = false;
  // Page context for jAIne (CU-868ky4r9h). Navigation metadata only, never page contents.
  private latestKnolliPageContext = '';
  private knolliFrame?: HTMLIFrameElement;
  private knolliFrameLoaded = false;
  private knolliContextRepostTimeout?: ReturnType<typeof setTimeout>;
  // The widget script sets its own onload on #knolli-embed, so this uses a listener and
  // does not replace that handler. The repost covers a receiver that registers after load.
  private readonly knolliFrameLoadListener = (): void => {
    this.knolliFrameLoaded = true;
    this.postKnolliPageContext();
    clearTimeout(this.knolliContextRepostTimeout);
    this.knolliContextRepostTimeout = setTimeout(() => this.postKnolliPageContext(), KNOLLI_CONTEXT_REPOST_MS);
  };
  private readonly jaineEscalationListener = (event: MessageEvent): void => {
    const frame = this.smarterCodesframe as HTMLIFrameElement;
    const targetOrigin = this.getFrameOrigin(frame);
    if (this.destroyed || !frame?.isConnected || event.source !== frame.contentWindow
      || !targetOrigin || event.origin !== targetOrigin || event.data?.type !== 'jaineEscalation') {
      return;
    }
    this.showEscalation = true;
    this.escalationMessage = event.data.message;
  };
  private readonly userIdRequestListener = (): void => this.postUserSession();

  @Output() public onJaineEscalation = new EventEmitter<NgxScrollEvent>();
  
  constructor(
    private switchBoard: SwitchBoard,
    private taxPlanningService: TaxPlanningService,
    private changeDetectorRef: ChangeDetectorRef,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.user = Session.getUser();

    this.currentEnvironment = environment;
    if (this.currentEnvironment.production){
      this.botUrl = "https://chat-tpiq-prod.smarter.codes";
    } else {
      this.botUrl = "https://chat-taxplaniq.smarter.codes";
    }
    if (this.changeDetectorRef){
      this.changeDetectorRef.detectChanges();
    }
    this.smarterCodesframe = document.querySelector("#smarterCodesframeId");
    window.addEventListener('message', this.jaineEscalationListener);
    window.addEventListener('getUserIdFromSession', this.userIdRequestListener);
    this.switchBoard.onPageChangeListener.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (data && this.smarterCodesframe?.isConnected && this.smarterCodesframe.contentWindow){
        const targetOrigin = this.getFrameOrigin(this.smarterCodesframe);
        if (!targetOrigin) {
          return;
        }
        this.smarterCodesframe.contentWindow.postMessage({
          type: 'pageChangeEvent',
          url: data.url,
          name: data.name,
          id: data.id
        }, targetOrigin);
      }
      if (data) {
        this.updateKnolliPageContext(data);
      }
    });
    this.smarterCodesframe.onload = () => {
      try{
        setTimeout(() => this.postUserSession(), 500);
      } catch (e) {}
    };

    // Add chatbot widget dynamically
    const buttonBackgroundUrl = `${window.location.origin}/images/jaine-bot.png`;
    
    /** Knolli Script Start */
      const hostUrl = window.location.host;
      const knolliUrl = this.currentEnvironment.KNOLLI_CHAT_URL;
      const aiAssistantId = this.currentEnvironment.jaineAiAssistantId;
      const knolliStageScript = this.renderer.createElement('script');
      this.knolliStageScript = knolliStageScript;
      const fileUploadParam = "&hideFileUpload=false";
      knolliStageScript.src =
        knolliUrl+"/content/css/widget.js" +
        "?url=" + encodeURIComponent(knolliUrl+"/knolli/"+aiAssistantId) +
        "&title=" + encodeURIComponent('Welcome to jAIne') +
        "&subtitle=" + encodeURIComponent('Your TaxPlanIQ Chief Empowerment Officer. Ask about tax planning, practice growth, or the screen you are on. jAIne can help you choose a next step, but may not see fields or client information unless you share them.') +
        "&placeholder=" +  encodeURIComponent('Ask jAIne a question') +
        "&host=" + encodeURIComponent(`${hostUrl}`) +
        "&icon=" + encodeURIComponent(`${buttonBackgroundUrl}`) +
        "&buttonBackground=" + encodeURIComponent(`url('${buttonBackgroundUrl}') no-repeat center / contain`) +
        "&headerText=jAIne" +
        "&welcomeMessage=true"+
        "&buttonText=''" +
        "&buttonHover=''" +
        "&buttonWidth=51px" +
        "&buttonHeight=60px" +
        "&buttonBorderRadius=0px" +
        "&buttonPosition_br=" + encodeURIComponent("44vh 6px") +
        "&fName=" + encodeURIComponent(`${this.user.first_name}`) +
        "&lName=" + encodeURIComponent(`${this.user.last_name}`) +
        "&orgName=" + encodeURIComponent(`${this.user.org}`) +
        "&uEmail=" + encodeURIComponent(`${this.user.id}`) +
        fileUploadParam;

      knolliStageScript.async = true;
      knolliStageScript.onload = () => this.onWidgetScriptSettled(true);
      knolliStageScript.onerror = () => this.onWidgetScriptSettled(false);
      // The widget script reads #pageContext when it runs, so set the context first.
      this.updateKnolliPageContext({url: window.location.pathname});
      this.renderer.appendChild(document.body, knolliStageScript);
    /** Knolli Script End */

      this.footerInterval = setInterval(() => {
        const footer = document.querySelector('.knolli-widget-footer');
        if (footer) {
          footer.textContent = 'jAIne uses AI and can make mistakes. Please double-check her responses.';
          clearInterval(this.footerInterval);
        }
      }, 500);
  }
  

  ngOnDestroy(): void {
    this.destroyed = true;
    window.removeEventListener('message', this.jaineEscalationListener);
    window.removeEventListener('getUserIdFromSession', this.userIdRequestListener);
    if (this.smarterCodesframe) {
      this.smarterCodesframe.onload = null;
    }
    if (this.footerInterval) {
      clearInterval(this.footerInterval);
    }
    clearTimeout(this.knolliContextRepostTimeout);
    this.knolliFrame?.removeEventListener('load', this.knolliFrameLoadListener);
    this.knolliFrame = undefined;
    // An async script still runs after its element is removed. Keep a pending script in the
    // page so its load handler can remove the widget it adds after this component is gone.
    if (this.knolliScriptSettled) {
      this.removeWidgetScript();
    }
    document.querySelectorAll(KNOLLI_WIDGET_SELECTORS.join(', '))
      .forEach(element => element.remove());
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  /**
   * Keeps one jAIne widget on the page when the widget script finishes. The script appends its
   * elements to <body>, so the last element of each kind belongs to the script that just ran.
   * A live component keeps that newest set. A destroyed component removes it, because a newer
   * chat bot owns the widget, or no chat bot is on the page.
   */
  onWidgetScriptSettled(loaded: boolean): void {
    this.knolliScriptSettled = true;
    if (loaded) {
      for (const selector of KNOLLI_WIDGET_SELECTORS) {
        const elements = Array.from(document.querySelectorAll(selector));
        const stale = this.destroyed ? elements.slice(-1) : elements.slice(0, -1);
        stale.forEach(element => element.remove());
      }
    }
    if (this.destroyed) {
      this.removeWidgetScript();
    } else if (loaded) {
      this.watchKnolliFrame();
    }
  }

  /** Posts the page context when the widget iframe that survived cleanup finishes its load. */
  private watchKnolliFrame(): void {
    const frame = document.getElementById('knolli-embed') as HTMLIFrameElement | null;
    if (!frame || frame === this.knolliFrame) {
      return;
    }
    this.knolliFrame?.removeEventListener('load', this.knolliFrameLoadListener);
    this.knolliFrame = frame;
    this.knolliFrameLoaded = false;
    frame.addEventListener('load', this.knolliFrameLoadListener);
  }

  updateKnolliPageContext(page: {url?: unknown; name?: string; id?: string | number}): void {
    // Some pages send ActivatedRoute.url, an Observable. Use the browser path for those.
    const rawRoute = typeof page?.url === 'string' && page.url ? page.url : window.location.pathname;
    const route = rawRoute.split(/[?#]/)[0];
    const context = JSON.stringify({
      source: 'TaxPlanIQ application',
      route,
      pageName: page?.name,
      pageId: page?.id,
      limitations: 'This is navigation metadata only. It does not include page contents, client data, or user-entered values.'
    });
    this.latestKnolliPageContext = context;

    let contextInput = document.getElementById('pageContext') as HTMLInputElement | null;
    if (!contextInput) {
      contextInput = this.renderer.createElement('input') as HTMLInputElement;
      contextInput.id = 'pageContext';
      contextInput.type = 'hidden';
      this.renderer.appendChild(document.body, contextInput);
    }
    contextInput.value = context;
    this.postKnolliPageContext();
  }

  /**
   * Sends the latest context to the loaded widget iframe. Before the load event, the post waits:
   * the load listener sends the latest context, so no navigation update is lost.
   */
  private postKnolliPageContext(): void {
    const frame = this.knolliFrame;
    const targetOrigin = frame ? this.getFrameOrigin(frame) : null;
    if (this.destroyed || !this.knolliFrameLoaded || !frame?.isConnected || !frame.contentWindow
      || !targetOrigin || !this.latestKnolliPageContext) {
      return;
    }
    frame.contentWindow.postMessage({type: 'pageContext', context: this.latestKnolliPageContext}, targetOrigin);
  }

  private removeWidgetScript(): void {
    if (this.knolliStageScript?.parentNode) {
      this.renderer.removeChild(this.knolliStageScript.parentNode, this.knolliStageScript);
    }
  }

  private postUserSession(): void {
    const frame = this.smarterCodesframe as HTMLIFrameElement;
    const targetOrigin = this.getFrameOrigin(frame);
    if (this.destroyed || !frame?.isConnected || !frame.contentWindow || !targetOrigin) {
      return;
    }
    frame.contentWindow.postMessage({
      type: 'userIdResponse',
      userId: Session.getUser().id,
      orgId: Session.getOrg(),
      userName: this.user.first_name + ' ' + this.user.last_name,
      isFirstLogin: this.taxPlanningService.getUserFirstLogin()
    }, targetOrigin);
  }

  private getFrameOrigin(frame: HTMLIFrameElement): string | null {
    try {
      return new URL(frame.src, window.location.href).origin;
    } catch (_) {
      return null;
    }
  }

  openChatbox() {
    this.showEscalation = false;
    this.showChatPannel = true;
  }

  closeChatbox() {
    this.showChatPannel = false;
  }

}
